import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  discountPresetsApi,
  type DiscountPreset,
  type DiscountPresetInput,
} from '@/lib/api/discount-presets.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

const emptyForm: DiscountPresetInput = {
  name: '',
  code: '',
  discountType: 'percent',
  value: 10,
  requiresApproval: false,
  minRole: null,
  appliesTo: 'all',
  requiresNote: false,
  active: true,
  sortOrder: 0,
  allStores: true,
};

export function DiscountPresetsSettingsPage() {
  const qc = useQueryClient();
  const listQ = useQuery({
    queryKey: ['discount-presets', 'all'],
    queryFn: () => discountPresetsApi.list({ includeInactive: true }),
  });
  const [editing, setEditing] = useState<DiscountPreset | null>(null);
  const [form, setForm] = useState<DiscountPresetInput>(emptyForm);
  const [showForm, setShowForm] = useState(false);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (editing) {
        return discountPresetsApi.update(editing.id, form);
      }
      return discountPresetsApi.create(form);
    },
    onSuccess: () => {
      toastSuccess(editing ? 'Preset updated' : 'Preset created');
      setShowForm(false);
      setEditing(null);
      setForm(emptyForm);
      void qc.invalidateQueries({ queryKey: ['discount-presets'] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const deactivateMut = useMutation({
    mutationFn: (id: string) => discountPresetsApi.remove(id),
    onSuccess: () => {
      toastSuccess('Preset deactivated');
      void qc.invalidateQueries({ queryKey: ['discount-presets'] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  function openCreate() {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  }

  function openEdit(row: DiscountPreset) {
    setEditing(row);
    setForm({
      name: row.name,
      code: row.code,
      description: row.description,
      discountType: row.discountType,
      value: Number(row.value),
      requiresApproval: row.requiresApproval,
      minRole: row.minRole as DiscountPresetInput['minRole'],
      appliesTo: row.appliesTo,
      maxUsesPerTicket: row.maxUsesPerTicket,
      requiresNote: row.requiresNote,
      active: row.active,
      sortOrder: row.sortOrder,
      allStores: row.storeId == null,
      storeId: row.storeId,
    });
    setShowForm(true);
  }

  const rows = listQ.data ?? [];

  return (
    <div className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-950">Discount Presets</h2>
          <p className="mt-1 text-sm text-slate-500">
            Quick discounts with optional approval rules for reporting.
          </p>
        </div>
        <button
          type="button"
          className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
          onClick={openCreate}
        >
          + Add preset
        </button>
      </div>

      {showForm ? (
        <div className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2">
          <label className="text-sm">
            Name
            <input
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </label>
          <label className="text-sm">
            Code
            <input
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              value={form.code ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
            />
          </label>
          <label className="text-sm">
            Type
            <select
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              value={form.discountType}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  discountType: e.target.value as 'percent' | 'fixed',
                }))
              }
            >
              <option value="percent">Percent</option>
              <option value="fixed">Fixed $</option>
            </select>
          </label>
          <label className="text-sm">
            Value
            <input
              type="number"
              step="0.01"
              min="0"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              value={form.value}
              onChange={(e) => setForm((f) => ({ ...f, value: Number(e.target.value) }))}
            />
          </label>
          <label className="text-sm">
            Applies to
            <select
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              value={form.appliesTo}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  appliesTo: e.target.value as DiscountPresetInput['appliesTo'],
                }))
              }
            >
              <option value="all">All</option>
              <option value="repairs">Repairs</option>
              <option value="parts">Parts</option>
              <option value="accessories">Accessories</option>
            </select>
          </label>
          <label className="text-sm">
            Minimum role
            <select
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              value={form.minRole ?? ''}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  minRole: e.target.value || null,
                }))
              }
            >
              <option value="">Any staff</option>
              <option value="front_desk">Front desk</option>
              <option value="technician">Technician</option>
              <option value="store_manager">Store manager</option>
              <option value="tenant_admin">Tenant admin</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input
              type="checkbox"
              checked={Boolean(form.requiresApproval)}
              onChange={(e) => setForm((f) => ({ ...f, requiresApproval: e.target.checked }))}
            />
            Requires approval
          </label>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input
              type="checkbox"
              checked={Boolean(form.requiresNote)}
              onChange={(e) => setForm((f) => ({ ...f, requiresNote: e.target.checked }))}
            />
            Requires note
          </label>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input
              type="checkbox"
              checked={Boolean(form.allStores)}
              onChange={(e) => setForm((f) => ({ ...f, allStores: e.target.checked }))}
            />
            Available at all stores
          </label>
          <label className="text-sm">
            Sort order
            <input
              type="number"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              value={form.sortOrder ?? 0}
              onChange={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))}
            />
          </label>
          <div className="flex gap-2 sm:col-span-2">
            <button
              type="button"
              className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              disabled={!form.name.trim() || saveMut.isPending}
              onClick={() => saveMut.mutate()}
            >
              Save
            </button>
            <button
              type="button"
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
              onClick={() => {
                setShowForm(false);
                setEditing(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-2 py-2">Name</th>
              <th className="px-2 py-2">Value</th>
              <th className="px-2 py-2">Approval</th>
              <th className="px-2 py-2">Active</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-b border-slate-100">
                <td className="px-2 py-3 font-medium text-slate-900">
                  {row.name}
                  {row.code ? (
                    <span className="ml-2 text-xs text-slate-400">{row.code}</span>
                  ) : null}
                </td>
                <td className="px-2 py-3">
                  {row.discountType === 'percent' ? `${row.value}%` : `$${row.value}`}
                </td>
                <td className="px-2 py-3">
                  {row.requiresApproval ? 'Yes' : 'No'}
                  {row.minRole ? ` · ${row.minRole}` : ''}
                </td>
                <td className="px-2 py-3">{row.active ? 'Yes' : 'No'}</td>
                <td className="px-2 py-3 text-right">
                  <button
                    type="button"
                    className="mr-2 font-semibold text-primary"
                    onClick={() => openEdit(row)}
                  >
                    Edit
                  </button>
                  {row.active ? (
                    <button
                      type="button"
                      className="font-semibold text-red-600"
                      onClick={() => deactivateMut.mutate(row.id)}
                    >
                      Deactivate
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-2 py-8 text-center text-slate-500">
                  No discount presets yet — create ones your store uses most.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
